import {
  ReportingMonetizationTierEnum,
  type ReportingMonetizationTier,
  type ReportingMonetizationTierPrice,
} from '@cast/types';

export type ReportingPlanId =
  | 'free'
  | 'starter'
  | 'team'
  | 'scale'
  | 'enterprise';

/** Paid self-serve plan ids that map to Stripe tiers. */
export type ReportingPaidPlanId = Exclude<
  ReportingPlanId,
  'free' | 'enterprise'
>;

export type ReportingTrialFleet = {
  /**
   * Period-average provisioned vCPU from overview timeseries
   * (approx. until billing-window average exists).
   */
  avgProvisionedVcpu?: number;
  clusterCount?: number;
  nodeCount?: number;
};

export const FREE_VCPU_CAP = 250;
export const SELF_SERVE_VCPU_CAP = 1500;
/** Default trial length used for UI copy and overview credits until BE sends dates. */
export const REPORTING_TRIAL_DAYS = 30;

/** Soft-nudge when average exceeds plan cap by this fraction (10%). */
export const OVERAGE_THRESHOLD_FRACTION = 0.1;

/** Route segments under organization management (compose with buildManagementUrl / SETTINGS_PATHS). */
export const REPORTING_ROUTE_SEGMENTS = {
  reporting: 'billing-report/reporting',
  plans: 'billing-report/reporting/plans',
} as const;

export type ReportingPlan = {
  id: ReportingPlanId;
  name: string;
  priceLabel: string;
  vcpuCapLabel: string;
  vcpuCap: number | null;
  cta: 'continue_free' | 'checkout' | 'contact_sales';
};

export const REPORTING_PLANS: ReportingPlan[] = [
  {
    id: 'free',
    name: 'Free',
    priceLabel: '$0 / mo',
    vcpuCapLabel: 'Up to 250 average vCPUs with basic reporting',
    vcpuCap: FREE_VCPU_CAP,
    cta: 'continue_free',
  },
  {
    id: 'starter',
    name: 'Starter',
    priceLabel: '$249 / mo',
    vcpuCapLabel: 'Up to 400 average vCPUs',
    vcpuCap: 400,
    cta: 'checkout',
  },
  {
    id: 'team',
    name: 'Team',
    priceLabel: '$499 / mo',
    vcpuCapLabel: 'Up to 900 average vCPUs',
    vcpuCap: 900,
    cta: 'checkout',
  },
  {
    id: 'scale',
    name: 'Scale',
    priceLabel: '$699 / mo',
    vcpuCapLabel: 'Up to 1,500 average vCPUs',
    vcpuCap: SELF_SERVE_VCPU_CAP,
    cta: 'checkout',
  },
];

export const resolveReportingPaidPlan = (
  planId: string | undefined
): ReportingPlan | undefined => {
  if (!planId) {
    return undefined;
  }
  const plan = REPORTING_PLANS.find((p) => p.id === planId);
  if (!(plan?.cta === 'checkout' && plan.vcpuCap != null)) {
    return undefined;
  }
  return plan;
};

export const ENTERPRISE_PLAN: ReportingPlan = {
  id: 'enterprise',
  name: 'Enterprise',
  priceLabel: 'Custom',
  vcpuCapLabel: 'Above 1,500 average vCPUs',
  vcpuCap: null,
  cta: 'contact_sales',
};

export const isOverPlanCap = (
  avgProvisionedVcpu: number,
  vcpuCap: number | null | undefined
): boolean =>
  vcpuCap != null && avgProvisionedVcpu > 0 && avgProvisionedVcpu > vcpuCap;

export const isSoftOverage = (
  avgProvisionedVcpu: number,
  planVcpuCap: number | null | undefined
): boolean => {
  if (planVcpuCap == null || planVcpuCap <= 0 || avgProvisionedVcpu <= 0) {
    return false;
  }
  return avgProvisionedVcpu > planVcpuCap * (1 + OVERAGE_THRESHOLD_FRACTION);
};

/** Format Stripe tier price for plan cards (e.g. `$249 / mo`). */
export const formatReportingTierPriceLabel = (
  price: ReportingMonetizationTierPrice
): string | undefined => {
  const unitAmount = Number(price.unitAmount);
  if (!Number.isFinite(unitAmount) || unitAmount < 0) {
    return undefined;
  }

  const major = unitAmount / 100;
  const currency = (price.currency ?? 'usd').toLowerCase();
  const amountLabel =
    currency === 'usd'
      ? `$${Number.isInteger(major) ? major : major.toFixed(2)}`
      : `${currency.toUpperCase()} ${
          Number.isInteger(major) ? major : major.toFixed(2)
        }`;

  const interval = (price.billingInterval ?? 'month').toLowerCase();
  const intervalLabel =
    interval === 'month' || interval === 'mo'
      ? 'mo'
      : interval === 'year' || interval === 'yr'
      ? 'yr'
      : interval;

  return `${amountLabel} / ${intervalLabel}`;
};

/** Smallest paid self-serve plan that covers the fleet, or enterprise above Scale. */
export const recommendReportingPlan = (
  avgProvisionedVcpu: number,
  plans: ReportingPlan[] = REPORTING_PLANS
): ReportingPlan => {
  if (
    !Number.isFinite(avgProvisionedVcpu) ||
    avgProvisionedVcpu <= 0 ||
    avgProvisionedVcpu <= FREE_VCPU_CAP
  ) {
    return plans.find((p) => p.id === 'free') ?? REPORTING_PLANS[0];
  }
  const paid = plans.find(
    (plan) =>
      plan.cta === 'checkout' &&
      plan.vcpuCap != null &&
      avgProvisionedVcpu <= plan.vcpuCap
  );
  return paid ?? ENTERPRISE_PLAN;
};

/**
 * After-trial explain dialog variants (Figma 27:45089 connectors):
 * - free: ≤250 → Free copy + View plans
 * - self_serve: 251–1500 → recommended paid plan copy + View plans
 * - enterprise: >1500 → Contact sales (+ View plans secondary)
 */
export type ReportingExplainDialogVariant =
  | 'free'
  | 'self_serve'
  | 'enterprise';

export const getReportingExplainDialogVariant = (
  avgProvisionedVcpu: number
): ReportingExplainDialogVariant => {
  if (
    !Number.isFinite(avgProvisionedVcpu) ||
    avgProvisionedVcpu <= FREE_VCPU_CAP
  ) {
    return 'free';
  }
  if (avgProvisionedVcpu <= SELF_SERVE_VCPU_CAP) {
    return 'self_serve';
  }
  return 'enterprise';
};

/** FE Scale plan ↔ BE Premium tier. */
const PLAN_ID_TO_TIER: Record<ReportingPaidPlanId, ReportingMonetizationTier> =
  {
    starter: ReportingMonetizationTierEnum.STARTER,
    team: ReportingMonetizationTierEnum.TEAM,
    scale: ReportingMonetizationTierEnum.PREMIUM,
  };

const TIER_TO_PLAN_ID: Partial<
  Record<ReportingMonetizationTier, ReportingPaidPlanId>
> = {
  [ReportingMonetizationTierEnum.STARTER]: 'starter',
  [ReportingMonetizationTierEnum.TEAM]: 'team',
  [ReportingMonetizationTierEnum.PREMIUM]: 'scale',
};

export const reportingPlanIdToTier = (
  planId: ReportingPlanId
): ReportingMonetizationTier | undefined => {
  if (planId === 'free' || planId === 'enterprise') {
    return undefined;
  }
  return PLAN_ID_TO_TIER[planId];
};

export const reportingTierToPlanId = (
  tier?: ReportingMonetizationTier | null
): ReportingPaidPlanId | undefined => {
  if (!tier) {
    return undefined;
  }
  return TIER_TO_PLAN_ID[tier];
};

/**
 * Overlay Stripe `/prices` onto static plan catalog. Free/Enterprise stay local;
 * missing tiers keep hardcoded fallbacks.
 */
export const applyReportingPricesToPlans = (
  prices: ReportingMonetizationTierPrice[] | undefined,
  plans: ReportingPlan[] = REPORTING_PLANS
): ReportingPlan[] => {
  if (!prices?.length) {
    return plans;
  }

  const labelByPlanId = new Map<ReportingPaidPlanId, string>();
  for (const price of prices) {
    const planId = reportingTierToPlanId(price.tier);
    if (!planId) {
      continue;
    }
    const label = formatReportingTierPriceLabel(price);
    if (label) {
      labelByPlanId.set(planId, label);
    }
  }

  if (labelByPlanId.size === 0) {
    return plans;
  }

  return plans.map((plan) => {
    if (plan.id === 'free' || plan.id === 'enterprise') {
      return plan;
    }
    const priceLabel = labelByPlanId.get(plan.id);
    return priceLabel ? { ...plan, priceLabel } : plan;
  });
};
